<template>
    <v-menu offset-y v-model="menu">
        <template v-slot:activator="{ props }">
            <v-list-item v-if="listitem != undefined" v-bind="props">
                <template v-slot:prepend>
                    <v-icon v-if="loading" color="accent"> mdi-loading mdi-spin </v-icon>
                    <v-icon v-else> mdi-account-hard-hat </v-icon>
                </template>
                <v-list-item-title>
                    user
                </v-list-item-title>
            </v-list-item>
            <v-card v-else variant="flat" class="border-thin w-100" v-bind="props"
                :loading="loading ? 'accent' : false" loader-height="2">
                <div class="d-flex">
                    <v-icon color="grey" class="ml-2 mr-1 my-3">
                        mdi-account-hard-hat
                    </v-icon>
                    <div class="ml-1 text-caption text-grey d-flex flex-column" style="min-width: 0">
                        <!-- Who the work is being filed under, and the username
                             only when there is nobody to name. Both at once would
                             print the same identity twice in the common case. -->
                        <span class="mt-1 d-inline-block text-truncate" style="max-width: 100%"
                            :class="{ 'text-accent font-weight-medium': naming_employees }">
                            {{ account_label }}
                        </span>
                        <span class="mb-1 d-inline-block text-truncate" style="max-width: 100%">
                            {{ production_station_name }}
                            [{{ production_line_name }}]
                        </span>
                    </div>
                </div>
            </v-card>
        </template>
        <v-list density="compact">
            <ButtonStaffChangeCompany v-if="i_am_staff" />
            <ButtonChangeEmployees @close="closeMenu" />
            <ButtonStation @close="closeMenu" />
            <!-- This is redundant -->
            <!-- <v-list-item v-if="management_permission || admin_permission" @click="toCompanyView">
                <template v-slot:prepend>
                    <v-icon> mdi-factory </v-icon>
                </template>
                <v-list-item-title> {{ t("company") }} </v-list-item-title>
            </v-list-item> -->
            <v-list-item @click="refreshBaseUserData">
                <template v-slot:prepend>
                    <v-icon> mdi-sync </v-icon>
                </template>
                <v-list-item-title>
                    {{ t("resync") }}
                </v-list-item-title>
            </v-list-item>
            <v-list-item v-if="is_android" @click="hardRefresh">
                <template v-slot:prepend>
                    <v-icon> mdi-power-plug-outline </v-icon>
                </template>
                <v-list-item-title>
                    {{ t("hard_resync") }}
                </v-list-item-title>
            </v-list-item>
            <ButtonForceDark />
            <v-list-item to="/account">
                <template v-slot:prepend>
                    <v-icon> mdi-account </v-icon>
                </template>
                <v-list-item-title>
                    {{ t("account") }}
                </v-list-item-title>
            </v-list-item>
            <v-list-item @click="logout">
                <template v-slot:prepend>
                    <v-icon> mdi-logout </v-icon>
                </template>
                <v-list-item-title>{{
                    t("logout")
                    }}</v-list-item-title>
            </v-list-item>
            <v-divider class="mt-1" />
            <!-- One line for the web app, one for the backend. The dot on the
                 API chip is the connection, and the chip turns red when it is
                 lost. The environment is the backend's, so its chip sits on
                 that line, and only when it is not production. -->
            <div class="px-3 py-1">
                <div class="d-flex">
                    <v-chip size="x-small" class="ma-1">
                        {{ front_label }}<span class="pit-mono ml-1">{{ build_version }}</span>
                    </v-chip>
                </div>
                <div class="d-flex">
                    <v-chip size="x-small" class="ma-1" :color="connetion_lost ? 'error' : undefined">
                        <v-icon start size="6" :color="connetion_lost ? 'error' : 'success'"> mdi-circle </v-icon>
                        API<span class="pit-mono ml-1">{{ api_version || "—" }}</span>
                    </v-chip>
                    <v-chip v-if="show_env" size="x-small" color="warning" class="ma-1">{{ frontend_env }}</v-chip>
                </div>
            </div>
        </v-list>
    </v-menu>
</template>


<script>
import ButtonStation from "/src/components/left_menu/ButtonStation";
import ButtonForceDark from "/src/components/left_menu/ButtonForceDark";
import PRODUUZIT_FRONT_ENV from "/src/utils/defaults";
import ButtonStaffChangeCompany from "./ButtonStaffChangeCompany.vue";
import ButtonChangeEmployees from "./ButtonChangeEmployees.vue";
import { useAppStore } from "@/stores/app";
import { useSessionStore } from "@/stores/session";
import { useTranslationsStore } from "@/stores/translations";
import { useSelectedStore } from "@/stores/selected";
import { useCloudstorageStore } from "@/stores/cloudstorage";

export default {
    setup() {
        const app_store = useAppStore()
        const session_store = useSessionStore()
        const selected_store = useSelectedStore()
        const cloudstorage_store = useCloudstorageStore()
        const translations_store = useTranslationsStore()
        const t = translations_store.translate
        return {
            app_store,
            session_store,
            selected_store,
            cloudstorage_store,
            t,
        }
    },
    components: { ButtonStation, ButtonForceDark, ButtonStaffChangeCompany, ButtonChangeEmployees },
    props: ["listitem"],
    data: () => ({ loading: false, menu: false, api_version: null }),
    watch: {
        // Asked on every open: the backend can be deployed mid-session.
        menu(open) {
            if (open) this.fetchApiVersion();
        },
    },
    computed: {
        connetion_lost() {
            return this.app_store.connection_lost;
        },
        viewer_mode() {
            return this.app_store.app_mode == "production";
        },
        i_am_staff() {
            return this.session_store.user_object?.auth?.pit_staff;
        },
        is_android() {
            return PRODUUZIT_FRONT_ENV.IS_ANDROID;
        },
        build_version() {
            return PRODUUZIT_FRONT_ENV.BUILD_VERSION;
        },
        frontend_env() {
          return this.session_store.user_object?.environment;
        },
        show_env() {
            return !!this.frontend_env && this.frontend_env !== "production";
        },
        // The version no longer says the platform, so the label does.
        front_label() {
            return this.is_android ? "Android" : "Web";
        },
        production_line_name() {
            let pl_name = this.session_store.selected_line_name;
            return pl_name || this.t("no_station");
        },
        production_station_name() {
            return this.session_store.selected_station_name;
        },
        username() {
            return this.session_store.user_object.username;
        },
        account_label() {
            return this.session_store.account_label;
        },
        // Accent only when the line is showing people: it changes per shift,
        // unlike the username it falls back to.
        naming_employees() {
            return !!this.session_store.selected_employees_label;
        },
        management_permission() {
            return this.session_store.user_object.auth
                ?.company_manager;
        },
        admin_permission() {
            return this.session_store.user_object.auth?.company_admin;
        },
    },
    methods: {
        async logout() {
            await this.session_store.signOut()
            this.$router
                .push({ name: 'login' })
                .catch(() =>
                    console.log(
                        "[!] Can't go to login. Maybe already in login page."
                    )
                );
        },
        toCompanyView() {
            this.$router.push("/company/settings");
            this.selected_store.setCompanyWide()
            this.selected_store.setProjectSelection("all")
        },
        switch_to_manage() {
            this.app_store.setManagerMode()
        },
        switch_to_viewer() {
            this.app_store.setViewerMode()
        },
        refreshBaseUserData() {
            this.loading = true;
            let proms = [
                this.app_store.initialLoad(),
                this.app_store.refreshBaseUserData(false),
                this.cloudstorage_store.clearCaches(),
            ]
            Promise.all(proms).finally(() => (this.loading = false));
        },
        hardRefresh() {
            location.reload();
        },
        closeMenu() {
            this.menu = false;
        },
        // GET /health/ needs no login and lives outside /api/v2/. A failure
        // keeps the last known value: offline is already on screen.
        async fetchApiVersion() {
            try {
                const url = new URL("/health/", PRODUUZIT_FRONT_ENV.ROOT_API);
                const response = await fetch(url, { cache: "no-store" });
                const body = await response.json();
                if (body.version) this.api_version = body.version;
            } catch {
                // nothing to do
            }
        },
    },
};
</script>
